import React, { useState, useEffect } from 'react';
import FileUploadStep from './ImportWizard/FileUploadStep';
import ColumnMappingStep from './ImportWizard/ColumnMappingStep';
import ValidationPreviewStep from './ImportWizard/ValidationPreviewStep';
import ImportExecutionStep from './ImportWizard/ImportExecutionStep';
import WizardProgress from './ImportWizard/WizardProgress';
import { ReferenceData } from '../../services/Referencedataservice';
import {
    getActiveSession,
    createSession,
    updateCurrentStep,
    saveCsvData,
    saveColumnMappings,
    saveValidatedRows,
    clearOldSessions,
    ImportSession,
    deleteAllSessions,
    updateSession,
} from '../../services/importSessionService';
import { fetchReferenceData } from '../../services/Referencedataservice';
import {
    getDemographicsGenderMode,
    getDemographicsRaceMode,
    getVisibleDemographicsFields,
} from '../../utils/demographicsSchema';
import toast from 'react-hot-toast';
import Button from '../UI/Button';
import './ImportWizard.css';

export interface CSVColumn {
    name: string;
    sampleData: string[];
}

export interface ColumnMapping {
    csvColumn: string;
    participantField: string;
    sourceType?: 'csv' | 'constant';
    constantValue?: 'today';
}

export interface ValidationError {
    row: number;
    field: string;
    message: string;
    value?: string;
}

export interface ValidatedRow {
    rowIndex: number;
    data: Record<string, any>;
    errors: ValidationError[];
    isValid: boolean;
}

export interface ImportResult {
    totalProcessed: number;
    successCount: number;
    failedCount: number;
    skippedCount: number;
    errors: Array<{
        row: number;
        data: Record<string, any>;
        errors: ValidationError[];
    }>;
}

const getReferenceDataDemographicsSignature = (referenceData: ReferenceData | null | undefined) => {
    const organization = referenceData?.organization;
    const genderOptions = referenceData && Array.isArray(referenceData.genderOptions) ? referenceData.genderOptions : [];
    const ethnicityOptions = referenceData && Array.isArray(referenceData.ethnicityOptions) ? referenceData.ethnicityOptions : [];
    const raceOptions = referenceData && Array.isArray(referenceData.raceOptions) ? referenceData.raceOptions : [];

    return JSON.stringify({
        visibleFields: [...getVisibleDemographicsFields(organization)].sort(),
        genderMode: getDemographicsGenderMode(organization),
        raceMode: getDemographicsRaceMode(organization),
        genderOptions: genderOptions.map(option => `${option.label}:${option.value}`),
        ethnicityOptions: ethnicityOptions.map(option => `${option.label}:${option.value}`),
        raceOptions: raceOptions.map(option => `${option.label}:${option.value}`),
    });
};

const hasReferenceDataForActiveDemographicsSchema = (
    cachedReferenceData: ReferenceData | undefined,
    latestReferenceData: ReferenceData
) => {
    if (
        !cachedReferenceData?.organization
        || !Array.isArray(cachedReferenceData.genderOptions)
        || !Array.isArray(cachedReferenceData.raceOptions)
    ) {
        return false;
    }

    return getReferenceDataDemographicsSignature(cachedReferenceData) === getReferenceDataDemographicsSignature(latestReferenceData);
};

interface ImportWizardProps {
    onBackToImportTypes?: () => void;
}

const ImportWizard: React.FC<ImportWizardProps> = ({ onBackToImportTypes }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [csvColumns, setCsvColumns] = useState<CSVColumn[]>([]);
    const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
    const [referenceData, setReferenceData] = useState<ReferenceData | null>(null);
    const [validatedRows, setValidatedRows] = useState<ValidatedRow[]>([]);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [restoringSession, setRestoringSession] = useState(false);

    const steps = [
        { number: 1, label: 'File Upload' },
        { number: 2, label: 'Column Mapping' },
        { number: 3, label: 'Validation Preview' },
        { number: 4, label: 'Import Summary' }
    ];

    // Initialize: Check for active session on mount
    useEffect(() => {
        const initializeWizard = async () => {
            setLoading(true);
            try {
                // Clear old completed/failed sessions
                await clearOldSessions();
                
                // Check for active session
                const activeSession = await getActiveSession();
                
                if (activeSession) {
                    await restoreSession(activeSession);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error initializing wizard:', error);
                toast.error('Failed to initialize import wizard');
                setLoading(false);
            }
        };
        
        initializeWizard();
    }, []);

    // Restore session from IndexedDB
    const restoreSession = async (session: ImportSession) => {

        if(session.currentStep==1){
            await deleteAllSessions();
            toast.success('Previous session deleted');
            setRestoringSession(false);
            setLoading(false);
            handleReset();
        } else {

            setRestoringSession(true);
            try {
                setSessionId(session.id);
                setCurrentStep(session.currentStep);
                
                if (session.csvData && session.csvColumns) {
                    setCsvData(session.csvData);
                    setCsvColumns(session.csvColumns);
                }
                
                if (session.columnMappings) {
                    setColumnMappings(session.columnMappings);
                }
                
                if (session.referenceDataCache) {
                    const latestReferenceData = await fetchReferenceData({ forceRefresh: true });
                    const cachedReferenceData = session.referenceDataCache as ReferenceData;
                    const restoredReferenceData = hasReferenceDataForActiveDemographicsSchema(cachedReferenceData, latestReferenceData)
                        ? cachedReferenceData
                        : latestReferenceData;

                    if (restoredReferenceData !== cachedReferenceData && session.currentStep > 2) {
                        setCurrentStep(2);
                        setReferenceData(restoredReferenceData);
                        setValidatedRows([]);
                        setImportResult(null);

                        await updateSession(session.id, {
                            currentStep: 2,
                            status: 'mapped',
                            referenceDataCache: restoredReferenceData,
                            validatedRows: undefined,
                            validationErrors: undefined,
                            importProgress: undefined,
                            importResult: undefined,
                            currentRow: undefined,
                        });

                        toast.success('Import session updated for the current demographics settings. Please review mappings before continuing.');
                        return;
                    }

                    setReferenceData(restoredReferenceData);
                }
                
                if (session.validatedRows) {
                    setValidatedRows(session.validatedRows);
                }
                
                if (session.importResult) {
                    setImportResult(session.importResult);
                }
                
                toast.success('Previous session restored');
            } catch (error) {
                console.error('Error restoring session:', error);
                toast.error('Failed to restore previous session');
            } finally {
                setRestoringSession(false);
                setLoading(false);
            }

        }
    };

    const handleFileUpload = async (file: File, data: any[], columns: CSVColumn[]) => {
        try {
            // Create new session
            const newSessionId = await createSession(file.name, file.size, file.type);
            setSessionId(newSessionId);
            
            // Save CSV data to session
            await saveCsvData(newSessionId, data, columns);
            
            // Update state
            setCsvFile(file);
            setCsvData(data);
            setCsvColumns(columns);
            
            // Move to next step
            await updateCurrentStep(newSessionId, 2);
            setCurrentStep(2);
        } catch (error) {
            console.error('Error saving file upload:', error);
            toast.error('Failed to save upload data');
        }
    };

    const handleColumnMapping = async (mappings: ColumnMapping[], refData: ReferenceData) => {
        try {
            if (!sessionId) {
                throw new Error('No active session');
            }
            
            // Save column mappings to session
            await saveColumnMappings(sessionId, mappings, refData);
            
            // Update state
            setColumnMappings(mappings);
            setReferenceData(refData);
            
            // Move to next step
            await updateCurrentStep(sessionId, 3);
            setCurrentStep(3);
        } catch (error) {
            console.error('Error saving column mappings:', error);
            toast.error('Failed to save column mappings');
        }
    };

    const handleValidation = async (validated: ValidatedRow[]) => {
        try {
            if (!sessionId) {
                throw new Error('No active session');
            }
            
            // Save validated rows to session
            await saveValidatedRows(sessionId, validated);
            
            // Update state
            setValidatedRows(validated);
            
            // Move to next step
            await updateCurrentStep(sessionId, 4);
            setCurrentStep(4);
        } catch (error) {
            console.error('Error saving validation:', error);
            toast.error('Failed to save validation data');
        }
    };

    const handleImportComplete = (result: ImportResult) => {
        setImportResult(result);
        // Import result is saved in ImportExecutionStep
    };

    const handleBack = async () => {
        if (currentStep > 1) {
            const newStep = currentStep - 1;

            if(newStep==1){
                await deleteAllSessions();
                toast.success('Previous session deleted');
                setRestoringSession(false);
                setLoading(false);
                handleReset();
            }

            setCurrentStep(newStep);
            if (sessionId) {
                try {
                    await updateCurrentStep(sessionId, newStep);
                } catch (error) {
                    console.error('Error updating step:', error);
                }
            }
        }
    };

    const handleReset = () => {
        setCsvFile(null);
        setCsvData([]);
        setCsvColumns([]);
        setColumnMappings([]);
        setReferenceData(null);
        setValidatedRows([]);
        setImportResult(null);
        setSessionId(null);
        setCurrentStep(1);
        // Session will be auto-cleaned by clearOldSessions
    };

    if (loading || restoringSession) {
        return (
            <div className="section">
                <div className="row justify-content-center">
                    <div className="column">
                        <div className="card margin-vertical-4">
                            <div className="card-body text-center">
                                <div className="margin-vertical-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="margin-top-3">
                                        {restoringSession ? 'Restoring previous session...' : 'Loading...'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="section">
            <div className="row justify-content-center">
                <div className="column">
                    <h1 className="form-header">Participant Data Import</h1>
                    {currentStep === 1 && onBackToImportTypes && <div className="wizard-import-type-back"><Button id="back-to-import-types" className="secondary" text="Choose a Different Import Type" onClick={onBackToImportTypes} /></div>}
                    
                    <WizardProgress steps={steps} currentStep={currentStep} />

                    {currentStep === 1 && (
                        <FileUploadStep
                            onNext={handleFileUpload}
                        />
                    )}

                    {currentStep === 2 && (
                        <ColumnMappingStep
                            csvColumns={csvColumns}
                            csvData={csvData}
                            onNext={handleColumnMapping}
                            onBack={handleBack}
                            savedMappings={columnMappings}
                            savedReferenceData={referenceData || undefined}
                        />
                    )}

                    {currentStep === 3 && referenceData && (
                        <ValidationPreviewStep
                            csvData={csvData}
                            columnMappings={columnMappings}
                            referenceData={referenceData}
                            onNext={handleValidation}
                            onBack={handleBack}
                            sessionId={sessionId}
                        />
                    )}

                    {currentStep === 4 && referenceData && (
                        <ImportExecutionStep
                            validatedRows={validatedRows}
                            columnMappings={columnMappings}
                            referenceData={referenceData}
                            onComplete={handleImportComplete}
                            onBack={handleBack}
                            onReset={handleReset}
                            sessionId={sessionId}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportWizard;
